import {
	ActivityIdRouteSync,
	OverlayProvider,
	ThemeProvider,
	TranslationProvider,
} from '@supply-mover-2-0/web-ui';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { BrandThemeApplier } from './components/BrandThemeApplier';
import { HelpChatWidget } from './components/HelpChatWidget';
import { LoginModal } from './components/LoginModal';
import { TenantGate } from './components/TenantGate';
import { PortalLayout } from './components/layout/PortalLayout';
import { AccountPage } from './pages/AccountPage';
import { CartPage } from './pages/CartPage';
import { CatalogPage } from './pages/CatalogPage';
import { ChangePasswordPage } from './pages/ChangePasswordPage';
import { ClientSelectorPage } from './pages/ClientSelectorPage';
import { DashboardPage } from './pages/DashboardPage';
import { ForgotPasswordPage } from './pages/ForgotPasswordPage';
import { HomePage } from './pages/HomePage';
import { InvoiceDetailPage } from './pages/InvoiceDetailPage';
import { InvoicesPage } from './pages/InvoicesPage';
import { OrderDetailPage } from './pages/OrderDetailPage';
import { OrdersPage } from './pages/OrdersPage';
import { ProductDetailPage } from './pages/ProductDetailPage';
import { PromoDetailPage } from './pages/PromoDetailPage';
import { RegisterPage } from './pages/RegisterPage';
import { SetPasswordPage } from './pages/SetPasswordPage';
import {
	getPreferredLanguage,
	getTranslations,
} from './services/portalLanguageService';
import { useLoginModalStore } from './stores/loginModalStore';

const queryClient = new QueryClient({
	defaultOptions: {
		queries: {
			staleTime: 30000,
			refetchOnWindowFocus: false,
		},
	},
});

/**
 * Connects the global login modal store to the LoginModal component.
 * Rendered inside TenantGate so it has access to the tenant context.
 */
function GlobalLoginModal(): JSX.Element {
	const { isOpen, message, close } = useLoginModalStore();
	return <LoginModal isOpen={isOpen} onClose={close} message={message} />;
}

function App() {
	return (
		<QueryClientProvider client={queryClient}>
			<BrowserRouter>
				<ActivityIdRouteSync />
				<ThemeProvider initialTheme="light">
					<TranslationProvider
						fetchTranslations={getTranslations}
						fetchPreferredLanguage={getPreferredLanguage}
					>
						<OverlayProvider>
							<TenantGate>
								{/* Side-effect component: applies brand colors,
								    favicon, and meta theme-color from the
								    portal_site bootstrap response. Renders nothing. */}
								<BrandThemeApplier />
								<GlobalLoginModal />
								{/* Customer-support widget floats across every
								    portal page. Mounts inside TenantGate so it
								    has tenant context, and inside PortalAuth's
								    reach so it can read the user's token. */}
								<HelpChatWidget />
								<Routes>
									{/* Client selector — separate page outside PortalLayout */}
									<Route
										path="/select-client"
										element={<ClientSelectorPage />}
									/>

									{/* Self-serve customer registration — public,
									    chrome-less, sits outside PortalLayout because
									    the visitor is by definition unauthenticated.
									    The page itself gates on portal_site
									    isRegistrationEnabled before showing the form. */}
									<Route path="/register" element={<RegisterPage />} />

									{/* Password recovery / new-user flows. Outside
									    PortalLayout so they render without nav chrome
									    (the user usually arrives via an emailed link
									    while signed out). */}
									<Route
										path="/forgot-password"
										element={<ForgotPasswordPage />}
									/>
									<Route
										path="/reset-password"
										element={<SetPasswordPage mode="reset" />}
									/>
									<Route
										path="/set-password"
										element={<SetPasswordPage mode="set-initial" />}
									/>

									{/* All portal routes wrapped in PortalLayout */}
									<Route path="/" element={<PortalLayout />}>
										<Route index element={<HomePage />} />
										<Route path="dashboard" element={<DashboardPage />} />
										<Route path="catalog" element={<CatalogPage />} />
										<Route path="product/:id" element={<ProductDetailPage />} />
										<Route path="promos/:id" element={<PromoDetailPage />} />
										<Route path="cart" element={<CartPage />} />
										<Route path="orders" element={<OrdersPage />} />
										<Route path="orders/:id" element={<OrderDetailPage />} />
										<Route path="invoices" element={<InvoicesPage />} />
										<Route
											path="invoices/:id"
											element={<InvoiceDetailPage />}
										/>
										<Route path="account" element={<AccountPage />} />
										<Route
											path="account/password"
											element={<ChangePasswordPage />}
										/>

										{/* Catch-all redirect to home */}
										<Route path="*" element={<Navigate to="/" replace />} />
									</Route>
								</Routes>
							</TenantGate>
						</OverlayProvider>
					</TranslationProvider>
				</ThemeProvider>
			</BrowserRouter>
		</QueryClientProvider>
	);
}

export default App;
